import { apiService } from './api';
import { LoginCredentials, LoginWithOtpCredentials, RegisterData, User, ApiResponse, LoginResponse, RegisterResponse, SendLoginOtpResponse } from '../types';

const CLIENT_APP = 'web_app';

const getBrowserInfo = (): string | null => {
  if (typeof navigator === 'undefined') return null;

  const userAgent = navigator.userAgent;
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  return 'Unknown';
};

const getWebClientMetadata = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      app: CLIENT_APP,
      platform: 'web',
    };
  }

  const resolvedOptions = Intl.DateTimeFormat().resolvedOptions();
  const platform = (navigator as any).userAgentData?.platform || navigator.platform || 'web';
  const isMobile =
    /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || window.innerWidth < 768;

  return {
    app: CLIENT_APP,
    platform: 'web',
    appVersion: process.env.REACT_APP_VERSION || 'web',
    os: platform,
    osVersion: 'unknown',
    device: isMobile ? 'Mobile' : 'Desktop',
    deviceModel: platform,
    browser: getBrowserInfo(),
    userAgent: navigator.userAgent,
    timezone: resolvedOptions.timeZone || null,
    locale: navigator.language || resolvedOptions.locale || null,
  };
};

export const login = async (credentials: LoginCredentials): Promise<ApiResponse<LoginResponse>> => {
  const response = await apiService.post('/auth/login', {
    ...credentials,
    client: getWebClientMetadata(),
  });
  return response.data;
};

export const sendLoginOtp = async (identifier: string): Promise<ApiResponse<SendLoginOtpResponse>> => {
  const response = await apiService.post('/auth/send-login-otp', {
    identifier,
    client: getWebClientMetadata(),
  });
  return response.data;
};

export const resendLoginOtp = async (identifier: string): Promise<ApiResponse<SendLoginOtpResponse>> => {
  const response = await apiService.post('/auth/resend-login-otp', {
    identifier,
    client: getWebClientMetadata(),
  });
  return response.data;
};

export const loginWithOtp = async (credentials: LoginWithOtpCredentials): Promise<ApiResponse<LoginResponse>> => {
  const response = await apiService.post('/auth/login-with-otp', {
    ...credentials,
    client: getWebClientMetadata(),
  });
  return response.data;
};

export const register = async (userData: RegisterData): Promise<ApiResponse<RegisterResponse>> => {
  const response = await apiService.post('/auth/register', {
    ...userData,
    client: getWebClientMetadata(),
  });
  return response.data;
};

export const registerCompany = async (companyData: RegisterData & { companyName: string }): Promise<ApiResponse<RegisterResponse>> => {
  const response = await apiService.post('/auth/register-company', {
    ...companyData,
    client: getWebClientMetadata(),
  });
  return response.data;
};

export const logout = async (): Promise<ApiResponse> => {
  const response = await apiService.post('/auth/logout');
  return response.data;
};

export const refreshToken = async (refreshToken: string): Promise<ApiResponse<{
  user: User;
  token: string;
  refreshToken: string;
}>> => {
  const response = await apiService.post('/auth/refresh-token', { refreshToken });
  return response.data;
};

export const getCurrentUser = async (): Promise<ApiResponse<User>> => {
  const response = await apiService.get('/auth/me');
  return response.data;
};

export const getPermissionContext = async (): Promise<ApiResponse<{
  user: User;
  companyUser: any;
  permissions: any;
}>> => {
  const response = await apiService.get('/auth/permission-context');
  return response.data;
};

export const updateProfile = async (userData: Partial<User>): Promise<ApiResponse<User>> => {
  const response = await apiService.put('/auth/update-profile', userData);
  return response.data;
};

export const changePassword = async (passwordData: {
  currentPassword: string;
  newPassword: string;
}): Promise<ApiResponse<{
  user: User;
  token: string;
  refreshToken: string;
}>> => {
  const response = await apiService.put('/auth/change-password', passwordData);
  return response.data;
};

export const forgotPassword = async (payload: { email?: string; phone?: string }): Promise<ApiResponse<{ message: string }>> => {
  const response = await apiService.post('/auth/forgot-password', payload);
  return response.data;
};

export const resetPassword = async (token: string, password: string): Promise<ApiResponse<{
  user: User;
  token: string;
  refreshToken: string;
}>> => {
  const response = await apiService.put(`/auth/reset-password/${token}`, { password });
  return response.data;
};

export const verifyEmail = async (token: string): Promise<ApiResponse<{ message: string }>> => {
  const response = await apiService.get(`/auth/verify-email/${token}`);
  return response.data;
};

export const verifyPhone = async (token: string): Promise<ApiResponse<{ message: string }>> => {
  const response = await apiService.get(`/auth/verify-phone/${token}`);
  return response.data;
};

export const resendVerificationEmail = async (email?: string): Promise<ApiResponse<{ message: string }>> => {
  const response = email 
    ? await apiService.post('/auth/resend-verification', { email })
    : await apiService.post('/auth/resend-verification');
  return response.data;
};

export const sendVerificationWhatsapp = async (phone: string): Promise<ApiResponse<{ message: string }>> => {
  const response = await apiService.post('/auth/send-verification-whatsapp', { phone });
  return response.data;
};

export const getAssigneeCode = async (): Promise<ApiResponse<{
  assigneeCode: string | null;
  generatedAt: string | null;
  isAssignee: boolean;
}>> => {
  const response = await apiService.get('/assignee/code');
  return response.data;
};

export const generateAssigneeCode = async (): Promise<ApiResponse<{
  assigneeCode: string;
  generatedAt: string;
}>> => {
  const response = await apiService.post('/assignee/generate-code');
  return response.data;
};

export default {
  login,
  register,
  logout,
  refreshToken,
  getCurrentUser,
  updateProfile,
  changePassword,
  forgotPassword,
  resetPassword,
  verifyEmail,
  resendVerificationEmail,
  verifyPhone,
  getAssigneeCode,
  generateAssigneeCode,
  sendVerificationWhatsapp,
};
